<script lang="ts">
  import { onDestroy } from "svelte";
  import { fade, fly } from "svelte/transition";
  import type { HueBridgeDto } from "@piano/shared";
  import { api } from "../../lib/api/index.js";
  import { hue } from "../../lib/stores/hue.svelte.js";
  import { session } from "../../lib/stores/session.svelte.js";
  import { ui } from "../../lib/stores/ui.svelte.js";
  import Icon from "../Icon.svelte";
  import Sheet from "../Sheet.svelte";

  /**
   * Hue Bridge verbinden – so untechnisch wie möglich:
   * Netzwerk durchsuchen → (sonst IP eingeben) → Knopf auf der Bridge drücken → fertig.
   * Ist die Bridge schon von jemand anderem gekoppelt, reicht ein Tipp.
   */
  let { onclose, ondone }: { onclose: () => void; ondone: (adjust: boolean) => void } = $props();

  const PAIR_SECONDS = 30;
  type Step = "search" | "found" | "manual" | "press" | "timeout" | "done";
  let step = $state<Step>("search");
  let bridges = $state<HueBridgeDto[]>([]);
  let ip = $state("");
  let probing = $state(false);
  let problem = $state<string | null>(null);
  let chosen = $state<HueBridgeDto | null>(null);
  let remaining = $state(PAIR_SECONDS);
  let pinnedCount = $state(0);
  let cancelled = false;

  void search();

  async function search(): Promise<void> {
    step = "search";
    problem = null;
    try {
      const [found] = await Promise.all([api.hueDiscover(), new Promise((r) => setTimeout(r, 900))]);
      bridges = found;
      step = found.length ? "found" : "manual";
    } catch (error) {
      ui.error(error);
      step = "manual";
    }
  }

  async function probe(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    probing = true;
    problem = null;
    try {
      const result = await api.hueProbe(ip.trim());
      if (result.ok) return void (await connect(result.bridge));
      problem =
        result.reason === "unreachable"
          ? `Die Musik-App erreicht unter ${ip.trim()} nichts. Wahrscheinlich blockiert die Firewall die Verbindung zwischen Server und Bridge (Port 443).` +
            (result.serverIp ? ` Server-Adresse: ${result.serverIp}.` : "")
          : `Unter ${ip.trim()} antwortet keine Hue Bridge. Bitte die Adresse in der Hue-App prüfen.`;
    } catch (error) {
      ui.error(error);
    } finally {
      probing = false;
    }
  }

  async function connect(bridge: HueBridgeDto): Promise<void> {
    chosen = bridge;
    problem = null;
    if (!bridge.paired) {
      step = "press";
      remaining = PAIR_SECONDS;
    }
    const started = Date.now();
    while (!cancelled) {
      try {
        const result = await api.hueConnect(bridge.id, bridge.ip);
        if (result.status === "connected") {
          hue.status = result.hue;
          pinnedCount = result.hue.pins.length;
          void hue.loadDock();
          step = "done";
          return;
        }
      } catch (error) {
        ui.error(error);
        step = bridges.length ? "found" : "manual";
        return;
      }
      const elapsed = (Date.now() - started) / 1000;
      remaining = Math.max(0, Math.ceil(PAIR_SECONDS - elapsed));
      if (elapsed >= PAIR_SECONDS) {
        step = "timeout";
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
  }

  onDestroy(() => (cancelled = true));

  const ring = 2 * Math.PI * 54;
</script>

<Sheet title="Philips Hue verbinden" {onclose} width={440}>
  <div class="setup">
    {#key step}
      <div class="step" in:fly={{ y: 16, duration: 350, delay: 120 }} out:fade={{ duration: 120 }}>
        {#if step === "search"}
          <div class="visual"><span class="radar"><Icon name="bridge" size={40} /></span></div>
          <h2>Suche nach deiner Hue Bridge …</h2>
          <p class="t2">Das dauert nur ein paar Sekunden.</p>
        {:else if step === "found"}
          <h2>{bridges.length === 1 ? "Bridge gefunden" : "Welche Bridge?"}</h2>
          <div class="list">
            {#each bridges as bridge (bridge.id)}
              <button type="button" class="bridge" onclick={() => connect(bridge)}>
                <span class="bridge-icon"><Icon name="bridge" size={26} /></span>
                <span class="grow">
                  <strong>{bridge.name}</strong>
                  <small class="t3"
                    >{bridge.paired
                      ? "Schon in der App gekoppelt – ein Tipp genügt"
                      : `Hue Bridge · ${bridge.ip}`}</small
                  >
                </span>
                <span class="btn btn-primary small">Verbinden</span>
              </button>
            {/each}
          </div>
          <button type="button" class="link" onclick={() => (step = "manual")}
            >Andere Bridge? IP-Adresse eingeben</button
          >
        {:else if step === "manual"}
          <h2>{bridges.length ? "IP-Adresse eingeben" : "Keine Bridge gefunden"}</h2>
          <p class="t2">
            Du findest die Adresse in der Hue-App unter <strong>Einstellungen → Bridges → ⓘ</strong>.
          </p>
          <form class="manual" onsubmit={probe}>
            <input
              class="input"
              bind:value={ip}
              inputmode="decimal"
              placeholder="z. B. 192.168.1.20"
              autocomplete="off"
              aria-label="IP-Adresse der Hue Bridge"
              required
            />
            <button type="submit" class="btn btn-primary" disabled={probing || !ip.trim()}
              >{probing ? "Prüfe …" : "Verbinden"}</button
            >
          </form>
          {#if problem}<p class="problem" transition:fade>{problem}</p>{/if}
          <button type="button" class="link" onclick={search}
            ><Icon name="refresh" size={16} /> Nochmal suchen</button
          >
        {:else if step === "press" || step === "timeout"}
          <div class="visual">
            <svg class="countdown" viewBox="0 0 120 120" aria-hidden="true">
              <circle cx="60" cy="60" r="54" class="track" />
              <circle
                cx="60"
                cy="60"
                r="54"
                class="progress"
                stroke-dasharray={ring}
                stroke-dashoffset={ring * (1 - remaining / PAIR_SECONDS)}
              />
            </svg>
            <span class="device" class:waiting={step === "press"}><span class="button"></span></span>
          </div>
          {#if step === "press"}
            <h2>Drück jetzt den runden Knopf oben auf der Bridge</h2>
            <p class="t2">Noch {remaining} Sekunden · {chosen?.name}</p>
          {:else}
            <h2>Kein Knopfdruck erkannt</h2>
            <p class="t2">Geh zur Bridge und versuch es noch einmal – der Knopf ist das große Feld oben.</p>
            <button type="button" class="btn btn-primary" onclick={() => chosen && connect(chosen)}
              >Nochmal versuchen</button
            >
          {/if}
        {:else}
          <div class="visual"><span class="done"><Icon name="check" size={44} stroke={3} /></span></div>
          <h2>Verbunden mit {chosen?.name ?? "der Bridge"}</h2>
          <p class="t2">
            {pinnedCount
              ? `${pinnedCount} ${pinnedCount === 1 ? "Szene ist" : "Szenen sind"} schon für den Notenständer ausgewählt.`
              : "Wähle jetzt aus, welche Szenen am Notenständer erscheinen."}
          </p>
          <div class="actions">
            <button type="button" class="btn btn-ghost" onclick={() => ondone(false)}>Passt so</button>
            <button type="button" class="btn btn-primary" onclick={() => ondone(true)}
              >Auswahl anpassen</button
            >
          </div>
        {/if}
      </div>
    {/key}
    {#if session.isAdmin && step === "manual"}
      <p class="hint t3">
        Der Server sucht in seinem eigenen Netz und über den Hue-Dienst. Weitere Netze: <code
          >HUE_SCAN_SUBNETS</code
        >.
      </p>
    {/if}
  </div>
</Sheet>

<style>
  .setup {
    display: grid;
    padding: 0 8px 8px;
    min-height: 340px;
  }
  .step {
    grid-area: 1 / 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
  }
  h2 {
    font-size: 22px;
    line-height: 1.2;
    max-width: 340px;
  }
  p {
    margin: 0;
    font-size: 15px;
    max-width: 360px;
  }
  .visual {
    position: relative;
    width: 140px;
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 8px 0 4px;
  }
  .radar {
    width: 84px;
    height: 84px;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--raised-2);
    color: var(--text);
    position: relative;
  }
  .radar::before,
  .radar::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 24px;
    box-shadow: 0 0 0 2px var(--accent);
    animation: radar 1800ms var(--ease-out) infinite;
  }
  .radar::after {
    animation-delay: 900ms;
  }
  @keyframes radar {
    from {
      opacity: 0.8;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(1.7);
    }
  }
  .list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  .bridge {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px;
    border-radius: var(--r-lg);
    background: var(--surface-2);
    text-align: left;
    transition:
      background var(--dur-fast),
      transform var(--dur-tap);
  }
  .bridge:hover {
    background: var(--raised);
  }
  .bridge:active {
    transform: scale(0.98);
  }
  .bridge-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: var(--raised-2);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .grow {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  small {
    font-size: 12px;
    font-weight: 600;
  }
  .btn.small {
    height: 36px;
    padding: 0 14px;
    font-size: 14px;
  }
  .link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--accent);
    font-weight: 700;
    font-size: 14px;
    padding: 8px;
  }
  .manual {
    display: flex;
    gap: 8px;
    width: 100%;
  }
  .manual .input {
    flex: 1;
    min-width: 0;
    font-variant-numeric: tabular-nums;
  }
  .problem {
    padding: 10px 14px;
    border-radius: var(--r-md);
    background: rgba(255, 122, 89, 0.12);
    color: var(--accent-2);
    font-size: 14px;
    font-weight: 600;
    text-align: left;
  }
  .countdown {
    position: absolute;
    inset: 0;
    transform: rotate(-90deg);
  }
  .countdown circle {
    fill: none;
    stroke-width: 6;
  }
  .track {
    stroke: var(--raised-2);
  }
  .progress {
    stroke: var(--accent);
    stroke-linecap: round;
    transition: stroke-dashoffset 1000ms linear;
  }
  /* Stilisierte Bridge von oben mit dem großen runden Knopf */
  .device {
    width: 74px;
    height: 74px;
    border-radius: 20px;
    background: #f4f3ef;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
  .button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #e3e1da;
    box-shadow: inset 0 0 0 2px #cfccc3;
  }
  .waiting .button {
    animation: press 1400ms var(--ease-out) infinite;
  }
  @keyframes press {
    0%,
    100% {
      box-shadow:
        inset 0 0 0 2px #cfccc3,
        0 0 0 0 rgba(77, 141, 255, 0.6);
    }
    45% {
      transform: scale(0.88);
      box-shadow:
        inset 0 0 0 2px var(--accent),
        0 0 0 14px rgba(77, 141, 255, 0);
    }
  }
  .done {
    width: 92px;
    height: 92px;
    border-radius: 50%;
    background: var(--accent-3);
    color: var(--on-accent-3);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: pop var(--dur-sheet) var(--ease-pop) both;
  }
  @keyframes pop {
    from {
      transform: scale(0.4);
      opacity: 0;
    }
  }
  .actions {
    display: flex;
    gap: 10px;
    margin-top: 6px;
  }
  .hint {
    grid-area: 2 / 1;
    margin-top: 14px;
    font-size: 12px;
    text-align: center;
  }
  @media (prefers-reduced-motion: reduce) {
    .radar::before,
    .radar::after,
    .waiting .button {
      animation: none;
    }
  }
</style>
